"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import api from "@/client/api/axios";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, Heart } from "lucide-react";

type WebsiteReview = {
  review_id: number;
  reviewer_name: string;
  rating: number;
  comment: string | null;
  created_at: string;
  vendor_name: string | null;
};

type StaticTestimonial = {
  name: string;
  role: string;
  rating: number;
  ratingValue?: number;
  jobs?: number;
  location?: string;
  comment: string;
  image: string;
  reviews?: string[];
};

interface LazyTestimonialsProps {
  staticFallback: StaticTestimonial[];
  staticReviewCount: string;
}

export function LazyTestimonials({ staticFallback, staticReviewCount }: LazyTestimonialsProps) {
  const [websiteReviews, setWebsiteReviews] = useState<WebsiteReview[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    api
      .get<{ success: boolean; data?: WebsiteReview[] }>("/reviews/website")
      .then((res) => {
        const data = res.data;
        if (data?.success && Array.isArray(data.data)) {
          setWebsiteReviews(data.data);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (loading || error) return;
    const list = websiteReviews && websiteReviews.length > 0 ? websiteReviews : staticFallback;
    if (list.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, 5000);
    return () => clearInterval(id);
  }, [loading, error, websiteReviews, staticFallback]);

  const useDynamic = !loading && !error && websiteReviews && websiteReviews.length > 0;
  const reviewCount = useDynamic ? String(websiteReviews!.length) : staticReviewCount;
  const listLength = useDynamic ? websiteReviews!.length : staticFallback.length;
  const hasCards = listLength > 0;

  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
      <div className="min-w-0 max-w-sm space-y-6 order-2 lg:order-1 text-left">
        <h3 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-foreground tracking-tight leading-tight">
          Loved by customers
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex gap-0.5" aria-label="4.5 out of 5 stars">
            {[1, 2, 3, 4].map((i) => (
              <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" aria-hidden />
            ))}
          </div>
          {loading ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            <span className="text-base font-semibold text-foreground">
              {reviewCount} Reviews
            </span>
          )}
        </div>
        <p className="text-foreground/80 text-base md:text-lg leading-relaxed">
          Rated by multifamily pros like you.
          <br />
          Add your favorite providers to your Pro Team.
        </p>
        {error && (
          <p className="text-sm text-muted-foreground" role="status">
            Reviews are temporarily unavailable.
          </p>
        )}
      </div>

      <div className="relative order-1 lg:order-2 min-h-[440px] sm:min-h-[480px]">
        {loading ? (
          <div className="relative flex items-center justify-center min-h-[440px] sm:min-h-[480px]">
            <Skeleton className="w-[260px] sm:w-[300px] h-[360px] rounded-2xl" />
          </div>
        ) : !hasCards ? (
          <div className="relative flex items-center justify-center min-h-[280px] rounded-2xl border border-dashed border-border bg-muted/30">
            <p className="text-sm text-muted-foreground">No reviews to show yet.</p>
          </div>
        ) : useDynamic ? (
          <div className="relative flex items-center justify-center min-h-[440px] sm:min-h-[480px]">
            {[-1, 0, 1].map((offset) => {
              const i = (index + offset + listLength) % listLength;
              const r = websiteReviews![i];
              if (!r) return null;
              const isCenter = offset === 0;
              return (
                <div
                  key={`slide-${offset}-${i}`}
                  className="absolute transition-all duration-300 ease-out will-change-transform"
                  style={{
                    zIndex: isCenter ? 30 : 20 - Math.abs(offset),
                    transform: `translateX(${offset * 100}%) scale(${isCenter ? 1 : 0.86})`,
                    opacity: isCenter ? 1 : 0.7,
                    pointerEvents: isCenter ? "auto" : "none",
                    filter: isCenter ? "none" : "brightness(0.97)",
                  }}
                >
                  <Card
                    className={`w-[260px] sm:w-[300px] rounded-2xl shadow-xl border border-border/50 overflow-hidden bg-card transition-all duration-300 ${
                      isCenter
                        ? "shadow-2xl ring-2 ring-primary/25 ring-offset-4 ring-offset-background scale-100"
                        : "shadow-lg"
                    }`}
                  >
                    <div className="p-6 pb-4">
                      <div className="flex flex-col items-center text-center">
                        <div className="relative mb-4 w-20 h-20 rounded-full bg-muted flex items-center justify-center ring-4 ring-background shadow-md">
                          <span className="text-2xl font-bold text-foreground/70">
                            {r.reviewer_name.charAt(0).toUpperCase()}
                          </span>
                          <span className="absolute -top-0.5 -right-0.5 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center border border-border/50" aria-hidden>
                            <Heart className="w-4 h-4 fill-rose-500 text-rose-500" />
                          </span>
                        </div>
                        <h4 className="font-bold text-foreground text-lg">{r.reviewer_name}</h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-foreground/70">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400 shrink-0" />
                          <span className="font-semibold text-foreground">{r.rating}</span>
                          {r.vendor_name && (
                            <>
                              <span>·</span>
                              <span className="text-foreground/60 truncate max-w-[140px]">{r.vendor_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="px-6 pb-6 pt-4 border-t border-border/60 bg-muted">
                      <p className="text-sm font-bold text-foreground/90 mb-2">Review</p>
                      {r.comment && (
                        <p className="text-sm text-foreground/75 leading-snug italic line-clamp-3">
                          &ldquo;{r.comment}&rdquo;
                        </p>
                      )}
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="relative flex items-center justify-center min-h-[440px] sm:min-h-[480px]">
            {[-1, 0, 1].map((offset) => {
              const i = (index + offset + staticFallback.length) % staticFallback.length;
              const t = staticFallback[i];
              if (!t) return null;
              const isCenter = offset === 0;
              return (
                <div
                  key={`slide-${offset}-${i}`}
                  className="absolute transition-all duration-300 ease-out will-change-transform"
                  style={{
                    zIndex: isCenter ? 30 : 20 - Math.abs(offset),
                    transform: `translateX(${offset * 92}%) scale(${isCenter ? 1 : 0.86})`,
                    opacity: isCenter ? 1 : 0.7,
                    pointerEvents: isCenter ? "auto" : "none",
                    filter: isCenter ? "none" : "brightness(0.97)",
                  }}
                >
                  <Card
                    className={`w-[260px] sm:w-[300px] rounded-2xl shadow-xl border border-border/50 overflow-hidden bg-card transition-all duration-300 ${
                      isCenter
                        ? "shadow-2xl ring-2 ring-primary/25 ring-offset-4 ring-offset-background scale-100"
                        : "shadow-lg"
                    }`}
                  >
                    <div className="p-6 pb-4">
                      <div className="flex flex-col items-center text-center">
                        <div className="relative mb-4">
                          <Image
                            src={t.image || "/placeholder.svg"}
                            alt={t.name}
                            width={80}
                            height={80}
                            className="w-20 h-20 rounded-full object-cover ring-4 ring-background shadow-md"
                          />
                          <span
                            className="absolute -top-0.5 -right-0.5 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center border border-border/50"
                            aria-hidden
                          >
                            <Heart className="w-4 h-4 fill-rose-500 text-rose-500" />
                          </span>
                        </div>
                        <h4 className="font-bold text-foreground text-lg">{t.name}</h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-foreground/70">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400 shrink-0" />
                          <span className="font-semibold text-foreground">{t.ratingValue ?? "—"}</span>
                          <span>·</span>
                          <span>{t.jobs ?? "—"} jobs</span>
                        </div>
                        <p className="text-xs text-foreground/60 mt-0.5">{t.location ?? "—"}</p>
                      </div>
                    </div>
                    <div className="px-6 pb-6 pt-4 border-t border-border/60 bg-muted">
                      <p className="text-sm font-bold text-foreground/90 mb-2">
                        {t.name.split(" ")[0]}'s Reviews
                      </p>
                      <div className="space-y-2.5">
                        {t.reviews?.slice(0, 2).map((review, j) => (
                          <p key={j} className="text-sm text-foreground/75 leading-snug italic line-clamp-2">
                            &ldquo;{review}&rdquo;
                          </p>
                        ))}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

